
import Vue from 'vue';

import * as CorpusStore from '@/store/search/corpus';
import * as UIStore from '@/store/search/ui';
import * as ResultsStore from '@/store/search/results/views';
import * as GlobalSearchSettingsStore from '@/store/search/results/global';
import * as SearchModule from '@/store/search/index';
import * as QueryStore from '@/store/search/query';
import * as FilterModule from '@/store/search/form/filters';

import { getAnnotationSubset, getMetadataSubset, isHitParams, spanFilterId } from '@/utils';
import { blacklab } from '@/api';

import {isHitResults, BLSearchResult, BLSearchParameters, BLHitResults, BLMatchInfoRelation, BLSummaryMatchInfo } from '@/types/blacklabtypes';
import { ContextLabel, ContextPositional, GroupBy, GroupByContext, humanizeGroupByOrSortBy, isValidGroupBy, parseGroupBy, serializeSortByOrGroupBy } from '@/utils/grouping';
import debug from '@/utils/debug';

// @ts-ignore
import Slider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css'
import jsonStableStringify from 'json-stable-stringify';

import SelectPicker, { OptGroup, Options } from '@/components/SelectPicker.vue';
import { CaptureAndRelation, HitToken, Option, TokenHighlight } from '@/types/apptypes';


import Tabs from '@/components/Tabs.vue';
import { getValueFunctions } from '@/components/filters/filterValueFunctions';
import { getHighlightColors, mergeMatchInfos } from '@/pages/search/results/table/hit-highlighting';
import { snippetParts } from '@/pages/search/results/table/table-layout';
import { corpusCustomizations } from '@/utils/customization';

// What we prefix the tag attribute grouping option with so we can recognize it
const OPT_PREFIX_SPAN_ATTRIBUTE = '$TAGATTR:';

function isSpanAttributeOptionValue(value: string): boolean {
	return value.startsWith(OPT_PREFIX_SPAN_ATTRIBUTE);
}

function spanAttributeOptionValue(name: string, attrName: string, listName?: string): string {
	const groupByName = listName ? `${listName}[${name}]` : name;
	return `${OPT_PREFIX_SPAN_ATTRIBUTE}${JSON.stringify([groupByName, attrName])}`;
}

function splitSpanAttributeOptionValue(value: string): { name: string, attrName: string } {
	if (value.startsWith(OPT_PREFIX_SPAN_ATTRIBUTE)) {
		// grouping on a span attribute
		const [name, attrName] = JSON.parse(value.slice(OPT_PREFIX_SPAN_ATTRIBUTE.length));
		return { name, attrName };
	}
	throw `Not a span attribute option value: ${value}`;
}

export default Vue.extend({
	components: {
		SelectPicker,
		Slider,
		Tabs
	},
	props: {
		type: String as () => 'hits'|'docs', // grouping hits or docs?
		disabled: Boolean,
		results: Object as () => BLSearchResult|undefined
	},
	data: () => ({
		/** The criteria the user has added to group on */
		addedCriteria: [] as GroupBy[],
		/** which of the addedCriteria is currently selected (to be edited on the right side) */
		selectedCriteriumIndex: 0,

		/** micro optimization: whether to skip next parse since the new value came from us anyway. */
		storeValueUpdateIsOurs: false,

		/** For the preview. Results from props can also be grouped, so we need to request these ourselves. */
		hits: undefined as undefined|BLHitResults,

		active: false,
	}),
	computed: {
		storeModule(): ResultsStore.ViewModule { return ResultsStore.getOrCreateModule(this.type); },
		/** NOTE: this may contain grouping criteria this corpus doesn't support! On page load, it comes directly from the URL! */
		storeValue(): string[] { return this.storeModule.getState().groupBy; },

		metadataGroups() { return CorpusStore.get.metadataGroups() },
		metadataFieldsMap() { return CorpusStore.get.allMetadataFieldsMap() },
		annotationGroups() { return CorpusStore.get.annotationGroups() },
		annotationsMap() { return CorpusStore.get.allAnnotationsMap() },
		defaultAnnotation(): string { 
			// sometimes grouping by the shown annotation itself isn't allowed (e.g. when it contains inline HTML)
			// in which case usually the corpus allows grouping on the plain version of that annotation.
			// so check if this one's allowed, and if not, find the first allowed one.
			const concordanceAnnotation = UIStore.getState().results.shared.concordanceAnnotationId; 
			const allowedAnnotations = UIStore.getState().results.shared.groupAnnotationIds;
			return allowedAnnotations.includes(concordanceAnnotation) ?
				concordanceAnnotation :
				(allowedAnnotations.length > 0 ? allowedAnnotations[0] : '');
		},

		metadataDropdownOptions(): Options {
			return (getMetadataSubset(
				UIStore.getState().results.shared.groupMetadataIds,
				this.metadataGroups,
				this.metadataFieldsMap,
				'Group',
				this,
				debug.debug, // is debug enabled - i.e. show debug labels in dropdown
				UIStore.getState().dropdowns.groupBy.metadataGroupLabelsVisible,
				corpusCustomizations.search.metadata.showField
			) as OptGroup[]).concat(this.tagAttributes)
			.map(optGroup => corpusCustomizations.group.customize(optGroup, this) ?? optGroup)
			.flatMap<Options[number]>(optGroup => optGroup.label ? optGroup : optGroup.options)
		},
		annotationDropdownOptions(): Options {
			return getAnnotationSubset(
				UIStore.getState().results.shared.groupAnnotationIds,
				this.annotationGroups,
				this.annotationsMap,
				'Search',
				this,
				CorpusStore.get.textDirection(),
				debug.debug, // is debug enabled - i.e. show debug labels in dropdown
				UIStore.getState().dropdowns.groupBy.annotationGroupLabelsVisible
			)
			.flatMap(optGroup => corpusCustomizations.group.customize(optGroup, this) ?? optGroup)
			.flatMap<Options[number]>(optGroup => optGroup.label ? optGroup : optGroup.options)
		},

		tabs(): Option[] {
			return this.addedCriteria.map((c, i) => ({
				label: humanizeGroupByOrSortBy(this, c, this.annotationsMap, this.metadataFieldsMap),
				value: i.toString(),
				class: isValidGroupBy(c) ? '' : 'text-muted',
			}));
		},

		firstHitPreviewQuery(): BLSearchParameters|undefined {
			let params = SearchModule.get.blacklabParameters();
			if (!isHitParams(params))
				return undefined; // can't get hits without a query

			params = {...params}; // make a copy before modifying
			if (!params.viewgroup)
				delete params.group;
			delete params.includetokencount;
			delete params.listvalues;
			// Only available for doc queries, remove it if present
			if (params.sort && params.sort.includes('numhits')) params.sort.split(',').filter(s => s != 'numhits').join(',');
			params.listmetadatavalues = '__nothing__';
			params.first = 0;
			params.number = 10; // not 1 but 10 because for parallel corpus we need a hit with otherFields (hopefully we'll get one)
			params.waitfortotal = false;
			return params;
		},
		firstHitPreviewQueryHash(): string {
			return this.active ? jsonStableStringify(this.firstHitPreviewQuery) : '';
		},
		contextsize(): number {
			let params = SearchModule.get.blacklabParameters();
			if (!isHitParams(params))
				return 5; // default
			return typeof params.context === 'number' ? params.context as number :  // use actual value from query if set
			    (typeof GlobalSearchSettingsStore.getState().context === 'number' ?
			        GlobalSearchSettingsStore.getState().context as number :  // use global default if set
			        5); // use default
		},

		captures(): { name: string, label: string, targetField: string|undefined }[] {
			const mi = this.hits?.summary?.pattern?.matchInfos;
			const sourceField = this.mainSearchField;
			return Object.entries(mi|| {})
				.filter(([k, v]) => v.type === 'span' && (v.fieldName ?? sourceField) === (this.selectedCriteriumAsContext?.fieldName ?? sourceField))
				.map(([k,v]) => {
					return {
						name: k,
						label: k,
						targetField: v.fieldName,
					}
				});
		},
		relations() {
			const mi = this.hits?.summary?.pattern?.matchInfos;
			const result: { name: string, label: string, targetField: string|undefined }[] = [];
			Object.entries(mi|| {})
				.filter(([k, v]) => v.type === 'relation')
				.forEach(([k,v]) => {
					const sourceInThisField = this.relationSourceInThisField(v);
					const targetInThisField = this.relationTargetInThisField(v);
					if (sourceInThisField || targetInThisField) {
						result.push({
							label: k,
							name: k,
							targetField: this.selectedCriteriumAsPositional?.fieldName,
						});
					}
				});
			return result;
		},
		tagAttributes(): OptGroup[] {
			/** Check if we should add this tag+attr option, and if so, add it. */
			const optAdd = (tagName: string, attributeName: string, listName?: string) => {
				// Check custom method to see if we should include this attribute
				// (or if that returns null, we will fall back to default behaviour)
				let shouldInclude = corpusCustomizations.group.includeSpanAttribute(tagName, attributeName);
				const filterId = spanFilterId(tagName, attributeName);
				const filter = FilterModule.getState().filters[filterId];
				if (shouldInclude === null) {
					// By default, you may group on any span attribute for which a
					// span filter exists, or which occurs in the within widget.
					const isSpanFilter = filter ? getValueFunctions(filter)?.isSpanFilter ?? null : false;
					const customWithin = corpusCustomizations.search.within;
					const isWithinAttr =
						customWithin.includeSpan(tagName) &&
						customWithin.includeAttribute(tagName, attributeName);
					shouldInclude = isSpanFilter || isWithinAttr;
				}
				if (shouldInclude) {
					const value = spanAttributeOptionValue(tagName, attributeName, listName);
					options.push({
						label: `${this.$t('results.table.groupBy', { field: filter ? this.$tMetaDisplayName(filter) : this.$tSpanAttributeDisplay(tagName, attributeName) })}`,
						value,
					});
				}
			}

			// Check if we have a list of matches (e.g. from withspans=true)
			const matchInfos = this.hits?.summary?.pattern?.matchInfos || {};
			const listEntry = Object.entries(matchInfos).find( ([name, mi]) => mi.type === 'list');
			let options: { label?: string, value: string, title?: string }[];
			const optGroups: OptGroup[] = [];
			if (listEntry) {
				// We capture lists of tags, but we don't know all the captured tags at this point.
				// Offer all span filters as grouping options, with a reference to the list,
				// (e.g. with-spans[ab] to mean "group on the ab tag in the with-spans list")
				const listName = listEntry[0];
				if (CorpusStore.getState().corpus!.relations.spans) {
					Object.entries(CorpusStore.getState().corpus!.relations.spans!)
						.forEach(([tagName, spanInfo]) => {
							if (spanInfo.attributes) {
								options = [];
								const attr = Object.keys(spanInfo.attributes);
								attr.forEach(attributeName => {
									optAdd(tagName, attributeName, listName);
								});
								if (options.length > 0) {
									optGroups.push({
										label: this.groupLabelTag(tagName),
										options: options,
									});
								}
							}
						});
				}
			} else {
				// We don't capture any lists. Only (possibly) include the tags we know we've captured.
				Object.entries(matchInfos)
					.filter(([_, matchInfo]) => matchInfo.type === 'tag')
					.forEach(([tagName, matchInfo]) => {
						const sourceInThisField = this.relationSourceInThisField(matchInfo);
						if (sourceInThisField) {
							options = [];
							const spanInfo = CorpusStore.getState().corpus!.relations.spans![tagName];
							const attr = Object.keys(spanInfo?.attributes ?? {});
							attr.forEach(attributeName => {
								optAdd(tagName, attributeName);
							});
							if (options.length > 0) {
								optGroups.push({
									label: this.groupLabelTag(tagName), //this.$t('results.groupBy.some_words.spanFiltersLabel').toString(),
									options: options,
								});
							}
						}
					});
			}
			return optGroups;
		},
		relationNames(): string[] {
			return this.relations.map(c => c.name);
		},
		showRelationPartWidget(): boolean {
			return this.selectedCriterium?.type === 'context' &&
				this.selectedCriterium.context.type === 'label' &&
				this.relationNames.includes(this.selectedCriterium.context.label)
		},

		mainSearchField(): string { return QueryStore.get.sourceField().id; },

		colors(): Record<string, TokenHighlight> {
			return this.hits ? getHighlightColors(this.hits.summary) : {};
		},

		selectedCriterium(): GroupBy|undefined { return this.addedCriteria[this.selectedCriteriumIndex]; },
		// Some utils to cast the current group to a specific type.
		// so we can use it in computeds for the template.
		/** When grouping on either: capture group, or relation source/target. */
		selectedCriteriumAsContext(): undefined|GroupByContext<ContextLabel|ContextPositional> {
			if (this.selectedCriterium?.type === 'context')
				return this.selectedCriterium as GroupByContext<ContextLabel|ContextPositional>;
		},
		selectedCriteriumAsLabel(): undefined|GroupByContext<ContextLabel> {
			if (this.selectedCriterium?.type === 'context' && this.selectedCriterium.context.type === 'label')
				return this.selectedCriterium as GroupByContext<ContextLabel>;
		},
		selectedCriteriumAsPositional(): undefined|GroupByContext<ContextPositional> {
			if (this.selectedCriterium?.type === 'context' && this.selectedCriterium.context.type === 'positional')
				return this.selectedCriterium as GroupByContext<ContextPositional>;
		},
		selectedCriteriumAsSlider(): undefined|GroupByContext<ContextPositional> {
			if (this.selectedCriteriumAsPositional?.context.whichTokens === 'specific')
				return this.selectedCriteriumAsPositional;
		},
		showCaseSensitive(): boolean {
			return this.selectedCriterium?.type === 'metadata' &&
				this.selectedCriterium.metadata.type === 'document';
		},

		sliderVisible(): boolean { return !!this.selectedCriteriumAsSlider; },
		sliderInverted(): boolean { const p = this.selectedCriteriumAsSlider?.context.position; return p === 'E' || p === 'B'; },
		sliderLabels(): any[] { return Array.from({length: this.contextsize}, (_, i) => i + 1).map(i => ({value: i, label: i})); },
		sliderValue: {
			get(): [number, number] { return [this.selectedCriteriumAsSlider?.context.start ?? 1, this.selectedCriteriumAsSlider?.context.end ?? 1]; },
			set(v: [number, number]) {
				if (this.selectedCriteriumAsSlider) {
					this.selectedCriteriumAsSlider.context.start = v[0];
					this.selectedCriteriumAsSlider.context.end = v[1];
				}
			}
		},

		preview(): {
			active: boolean;
			word: string;
			wordAsHtml: boolean;
			selectedAnnotation: string;
			selectedAnnotationAsHtml: boolean;
			punct: string;
			style: object;
			captureAndRelation: CaptureAndRelation[]|undefined;
		}[][] {
			if (this.selectedCriterium?.type !== 'context' ||
				!isHitResults(this.hits) ||
				!this.hits.hits.length) {
					return [];
			}

			const wordAnnotation = UIStore.getState().results.shared.concordanceAnnotationId;
			const wordAsHtml = UIStore.getState().results.shared.concordanceAsHtml;
			
			const firstHit = this.hits.hits.find(v => !!v.otherFields) ?? this.hits.hits[0];
			const targetField = this.selectedCriterium?.fieldName;
			const hitInField = targetField && targetField.length > 0 && targetField !== this.mainSearchField && firstHit.otherFields ? firstHit.otherFields[targetField] : firstHit;
			const {annotation, context} = this.selectedCriterium;

			const snippet = snippetParts(hitInField, this.colors)

			// Don't highlight the list of relations matchInfo; it doesn't make sense to group on those
			const removeListMatchInfo = (t: HitToken) => t.captureAndRelation = t.captureAndRelation?.filter(c => c.key.indexOf('[') < 0);
			snippet.before.forEach(removeListMatchInfo);
			snippet.match.forEach(removeListMatchInfo);
			snippet.after.forEach(removeListMatchInfo);

			const position = context.type === 'positional' ? context.position : undefined;

			// Now extract the indices of the tokens that are active (i.e. being grouped on).
			// start and end here are INCLUSIVE and 0-indexed. While start + end in the GroupBy object are 1-indexed.
			// If we're not grouping on a specific word, we'll just show the entire snippet without anything highlighted.
			let start =  Number.MAX_SAFE_INTEGER;
			let end = -Number.MAX_SAFE_INTEGER;
			if (context.type === 'positional') {
				const whichTokens = context.whichTokens;
				if (whichTokens === 'all') { start = 0; end = Number.MAX_SAFE_INTEGER; }
				else if (whichTokens === 'first') { start = 0; end = 0; }
				else { start = context.start! - 1; end = context.end! - 1; }

				// left/before context ('B') and hit-from-end context ('E') use inverted index in BlackLab, mimic this.
				if (position === 'E' || position === 'B') {
					const sectionLength = position === 'E' ? snippet.match.length : snippet.before.length;
					// subtract 1 because array.length is 1 more than the last index.
					const tmp = start;
					start = sectionLength - end - 1;
					end = sectionLength - tmp - 1;
				}
			}

			const isActiveIndex = (i: number): boolean => i >= start && i <= end;

			const isActiveRelationOrCapture = (t: HitToken): boolean => {
				/** might be null if not grouping on a capture at the moment */
				const currentlyGroupedOnCaptureOrRelation =  t.captureAndRelation?.find(c => c.key === this.selectedCriteriumAsLabel?.context.label);
				if (!currentlyGroupedOnCaptureOrRelation) return false;

				if (this.selectedCriteriumAsLabel?.context.relation === 'source') { return currentlyGroupedOnCaptureOrRelation.isSource; }
				else if (this.selectedCriteriumAsLabel?.context.relation === 'target') { return currentlyGroupedOnCaptureOrRelation.isTarget; }
				else return true;
			}

			const getPreviewStyle = (t: HitToken): object => {
				return t.captureAndRelation?.length ? {
					background: `linear-gradient(90deg, ${t.captureAndRelation.map((c, i) => `${c.highlight.color} ${i / t.captureAndRelation!.length * 100}%, ${c.highlight.color} ${(i + 1) / t.captureAndRelation!.length * 100}%`)})`,
					color: t.captureAndRelation[0].highlight.textcolor,
					textShadow: `0 0 1.25px ${t.captureAndRelation[0].highlight.textcolorcontrast},`.repeat(10).replace(/,$/, ''),
					cursor: 'pointer',
				} : {}
			}

			return [
				snippet.before.map((t, i) => ({
					word: (t.punctBefore || '') + t.annotations[wordAnnotation] || '·',
					wordAsHtml,
					selectedAnnotation: t.annotations[annotation!] || '·',
					selectedAnnotationAsHtml: annotation === wordAnnotation ? wordAsHtml : false,
					punct: t.punct,
					active: (position === 'B' && isActiveIndex(i)) || isActiveRelationOrCapture(t),
					style: getPreviewStyle(t),
					captureAndRelation: t.captureAndRelation,
				})),
				snippet.match.map((t, i) => ({
					word: (t.punctBefore || '') + t.annotations[wordAnnotation] || '·',
					wordAsHtml,
					selectedAnnotation: t.annotations[annotation!] || '·',
					selectedAnnotationAsHtml: annotation === wordAnnotation ? wordAsHtml : false,
					punct: t.punct,
					active: ((position === 'H' || position === 'E') && isActiveIndex(i)) || isActiveRelationOrCapture(t),
					style: getPreviewStyle(t),
					captureAndRelation: t.captureAndRelation,
				})),
				snippet.after.map((t, i) => ({
					word: (t.punctBefore || '') + t.annotations[wordAnnotation] || '·',
					wordAsHtml,
					selectedAnnotation: t.annotations[annotation!] || '·',
					selectedAnnotationAsHtml: annotation === wordAnnotation ? wordAsHtml : false,
					punct: t.punct,
					active: (position === 'A' && isActiveIndex(i)) || isActiveRelationOrCapture(t),
					style: getPreviewStyle(t),
					captureAndRelation: t.captureAndRelation,
				}))
			];
		},

		contextOptions(): Options {
			return [{
				label: this.$t('results.groupBy.some_words.theFirstWord').toString(),
				value: 'first'
			}, {
				label: this.$t('results.groupBy.some_words.allWords').toString(),
				value: 'all'
			}, {
				label: this.$t('results.groupBy.some_words.specificWords').toString(),
				value: 'specific'
			}, {
				label: this.$t('results.groupBy.some_words.captureGroupsLabel').toString(),
				options: [
					...this.relations.map(c => ({
						label: `<span class="color-ball" style="background-color: ${this.colors[c.label].color};">&nbsp;</span> relation ${c.name}`,
						value: c.name
					})),
					...this.captures.map(c => ({
						label: `<span class="color-ball" style="background-color: ${this.colors[c.label].color};">&nbsp;</span> capture ${c.name}`,
						value: c.name
					})),
				]
			}];
		},
		fieldName: {
			get(): string { return this.selectedCriteriumAsContext?.fieldName ?? this.mainSearchField; },
			set(v: string) {
				if (this.selectedCriteriumAsContext) {
					this.selectedCriteriumAsContext.fieldName = v;
					if (this.selectedCriteriumAsContext.context.type === 'label') {
						const selectedContext = this.selectedCriteriumAsContext.context as ContextLabel;
						const selectedLabel = selectedContext.label;
						// When contextOptions has updated, we need to check if the selected label is still in the list.
						// The reason we need to do this is that when the user selects a different field
						// The relations might not be available in the new field.
						// So we need to clear the value
						// We can't rely on the Selectpicker component to do this
						// because if we would make that reject unknown values
						// it would clear the value when results are still pending
						// (as the relations are not available yet)
						Vue.nextTick(() => {
							const opt = this.contextOptions.find(o => {
								if (typeof o === 'string') {
									return o === selectedLabel;
								} else if ((o as OptGroup).options) {
									return !!((o as OptGroup).options.find(o => {
										const opt = (o as Option).value ?? o;
										return opt === selectedLabel;
									}));
								} else {
									return (o as Option).value === selectedLabel;
								}
							});
							if (opt) {
								// Option is still in the list after changing field
								const relPart = this.getInitialRelationPartValue(selectedLabel);
								if (relPart) {
									// There's only one relation part in the selected field; so set it.
									selectedContext.relation = relPart;
								}
							} else {
								// Option is no longer in this list after changing field;
								// set to "all words".
								selectedContext.label = 'all';
							}
						})
					}
				}
			}
		},
		contextValue: {
			/** The string value is when grouping on a capture group or relation. */
			get(): 'first'|'all'|'context'|string {
				// if grouping on a label: return the label, if grouping on a position: return the position.
				// Otherwise blank.
				if (this.selectedCriterium?.type !== 'context')
					return '';
				else if (this.selectedCriterium.context.type === 'label')
					return this.selectedCriterium.context.label;
				else
					return this.selectedCriteriumAsPositional?.context.whichTokens ?? '';
			},
			/** The string value is when grouping on a capture group or relation. */
			set(v: 'first'|'all'|'specific'|string) {
				if (this.selectedCriterium?.type !== 'context') return;

				// should never happen we receive one of these options when type is not 'positional'
				// but make typescript happy.
				if (v === 'first' || v === 'all' || v === 'specific') {
					if (this.selectedCriteriumAsPositional) {
						this.selectedCriteriumAsPositional.context.whichTokens = v;
					} else {
						// update context object as we're currently grouping on a label.
						this.selectedCriterium.context = {
							type: 'positional',
							position: 'H',
							whichTokens: v,
							start: 1,
							end: this.contextsize,
						}
					}
					// if we're grouping on the entire hit, we can't group from the end. (blacklab limitation)
					if (v === 'all' && this.selectedCriteriumAsPositional?.context.position === 'E') {
						this.selectedCriteriumAsPositional.context.position = 'H';
					}
				} else {
					// update context object as we're currently grouping on a label.
					this.selectedCriterium.context = {
						type: 'label',
						label: v,
						relation: this.relationNames?.includes(v) ? this.getInitialRelationPartValue(v) : undefined
					}
				}
			},
		},
		selectedMetadataCriterium: {
			get(): string {
				if (this.selectedCriterium?.type === 'metadata') {
					const meta = this.selectedCriterium.metadata;
					if (meta.type === 'document')
						return meta.field;
					else
						return spanAttributeOptionValue(meta.spanName, meta.attributeName);
				}
				return '';
			},
			set(v: string) {
				if (v && this.selectedCriterium?.type === 'metadata') {
					const isSpanAttr = isSpanAttributeOptionValue(v);
					if (!isSpanAttr) {
						this.selectedCriterium.metadata ={
							type: 'document',
							field: v,
						};
					} else {
						const { name, attrName } = splitSpanAttributeOptionValue(v);
						this.selectedCriterium.metadata ={
							type: 'span-attribute',
							spanName: name,
							attributeName: attrName,
						};
					}
				}
			}
		},

		positionOptions(): Options {
			if (!(this.selectedCriterium?.type === 'context' && this.selectedCriterium.context.type === 'positional')) return [];

			return [
			{ label: this.$t('results.groupBy.in_this_location.beforeTheHit').toString(), value: 'B'},
			{ label: this.$t('results.groupBy.in_this_location.inTheHit').toString(), value: 'H' },
			// grouping from the end of the hit when grouping on entire hit is not possible (causes an exception in BlackLab)
			...(this.selectedCriterium?.context.whichTokens !== 'all' ? [{label: this.$t('results.groupBy.in_this_location.fromTheEnd').toString(), value: 'E'}] : []),
			{ label: this.$t('results.groupBy.in_this_location.afterTheHit').toString(), value: 'A' }];
		},
		positionValue: {
			get(): 'B'|'H'|'E'|'A' { return this.selectedCriterium?.type === 'context' && this.selectedCriterium.context.type === 'positional' ? this.selectedCriterium.context.position : 'H'; },
			set(v: 'B'|'H'|'E'|'A') {
				if (this.selectedCriterium?.type === 'context' && this.selectedCriterium.context.type === 'positional')
					this.selectedCriterium.context.position = v ;
			}
		},


		isParallel(): boolean { return CorpusStore.get.isParallelCorpus() ?? false; },

		parallelVersionOptions(): Option[] {
			// NOTE: we look at results.summary.pattern, not the QueryStore, so this also works
			//       with Expert queries where the target version is not selected
			//       in the GUI but part of the query.
			const patt = this.results?.summary.pattern;
			const fields = patt ? [patt.fieldName, ...(patt.otherFields ?? [])] : [];
			return fields.map(fieldName => CorpusStore.get.parallelAnnotatedFieldsMap()[fieldName]).map(field => ({
				value: field.id,
				label: this.$tAnnotatedFieldDisplayName(field)
			}));
		}
	},
	methods: {
		groupLabelTag(tagName: string): string {
			if (this.$te(`results.groupBy.groupLabel.tag ${tagName}`))
				return this.$t(`results.groupBy.groupLabel.tag ${tagName}`).toString();
			return this.$td(`index.spans.${tagName}`, `Tag ${tagName}`);
		},
		apply() {
			this.storeValueUpdateIsOurs = true;
			this.storeModule.actions.groupBy(serializeSortByOrGroupBy(this.addedCriteria.filter(isValidGroupBy)));

			// JN disabled next line; a tabbed interface with no tab selected is
			//    normally impossible in a GUI and looks confusing/broken.
			//    (maybe this was done to show that the search is being carried out?
			//     maybe figure a better way of signalling this, i.e. scroll to results?)
			//this.selectedCriteriumIndex = -1;
		},

		isEmptyGroup(group: GroupBy) {
			return (group.type === 'context' && !group.annotation) ||
				(group.type === 'metadata' && (
					group.metadata.type === 'document' && !group.metadata.field ||
					group.metadata.type === 'span-attribute' && !group.metadata.spanName && !group.metadata.attributeName));
		},
		isInvalidGroup(group: GroupBy) { return !this.isEmptyGroup(group) && !isValidGroupBy(group); },
		removeGroup(i: number) {
			if (this.selectedCriteriumIndex >= i) this.selectedCriteriumIndex--;
			this.addedCriteria.splice(i, 1);
		},
		clear() {
			this.addedCriteria = [];
			this.selectedCriteriumIndex = -1;
			this.active = false;
			if (this.storeValue.length > 0)
				this.apply(); // only when actually removing something, otherwise we reset the results for no reason.
		},
		addAnnotation() {
			this.addedCriteria.push({
				type: 'context',
				fieldName: this.mainSearchField,
				annotation: this.defaultAnnotation,
				context: {
					type: 'positional',
					position: 'H',
					whichTokens: 'all',
					start: 1,
					end: this.contextsize
				},
				caseSensitive: false
			});
			this.selectedCriteriumIndex = this.addedCriteria.length - 1;
		},
		addMetadata() {
			this.addedCriteria.push({
				type: 'metadata',
				caseSensitive: false,
				metadata: {
					type: 'document',
					field: ''
				}
			});
			this.selectedCriteriumIndex = this.addedCriteria.length - 1;
		},
		/**
		 * When a highlighted word in the preview is clicked, retrieve what it represents (a capture group, or relation source/target)
		 * And update the current grouping criterium to be that.
		 *
		 * When a word has multiple things it represents, use the position of the click to determine which one was clicked.
		 * When a relation is clicked and it's already the current relation, toggle between source/target and full context.
		 */
		handlePreviewClick(event: MouseEvent, section: number, index: number) {
			const preview = this.preview[section][index];
			if (!preview.captureAndRelation?.length || this.selectedCriterium?.type !== 'context') return;

			const elementRect = (event.target as HTMLElement).getBoundingClientRect();
			const elementLeftBorder = elementRect.left + window.scrollX;
			const clickPositionInElement = event.pageX - elementLeftBorder;
			const elementWidth = elementRect.width;

			// sometimes a click slightly outside the element can cause a negative or overflow value, so clamp it.
			const relationIndex = Math.max(0, Math.min(Math.floor((clickPositionInElement / elementWidth) * preview.captureAndRelation.length), preview.captureAndRelation.length - 1));
			const relation = preview.captureAndRelation[relationIndex];

			this.selectedCriterium.context = {
				type: 'label',
				label: relation.key,
				relation: relation.isSource ? 'source' : relation.isTarget ? 'target' : undefined,
			}
		},
		relationPartByClass(part: 'source'|'target'|'label'): string {
			const relName = this.selectedCriteriumAsLabel?.context.label;
			const relation = relName ? this.hits?.hits[0].matchInfos?.[relName] as BLMatchInfoRelation : null;
			const relClass = relation?.relClass ?? null;
			if (relClass) {
				// Get the specific name for this relClass;
				// i.e. 'head' instead of 'source' for the 'dep' relationClass (dependency relations)
				const key = `results.groupBy.relationPartByClass.${relClass}.${part}`;
				if (this.$te(key))
					return this.$t(key).toString();
			}
			// No specific name for this relation class; fall back to the default relation part name.
			return this.$t(`results.groupBy.relationPartByClass.default.${part}`).toString();
		},
		relationSourceInThisField(v: BLSummaryMatchInfo) {
			const field = v.fieldName ?? this.mainSearchField;
			return !this.selectedCriteriumAsContext?.fieldName || field === this.selectedCriteriumAsContext?.fieldName;
		},
		relationTargetInThisField(v: BLSummaryMatchInfo) {
			const field = v.targetField ?? this.mainSearchField;
			return !this.selectedCriteriumAsContext?.fieldName || field === this.selectedCriteriumAsContext?.fieldName;
		},
		relationMatchInfoDefByLabel(label: string): BLSummaryMatchInfo {
			const mi = this.hits?.summary?.pattern?.matchInfos ?? {};
			return mi[label] ?? { type: 'span' };
		},
		// If relation only has source or target in this field, select that by default
		getInitialRelationPartValue(relationName: string) {
			const matchInfoDef = this.relationMatchInfoDefByLabel(relationName);
			const source = this.relationSourceInThisField(matchInfoDef);
			const target = this.relationTargetInThisField(matchInfoDef);
			return source == target ? undefined : (source ? 'source' : 'target');
		},
	},
	watch: {
		storeValue: {
			immediate: true,
			handler() {
				if (this.storeValueUpdateIsOurs) {
					this.storeValueUpdateIsOurs = false;
					return;
				}
				this.addedCriteria = parseGroupBy(this.storeValue, this.results);
				this.active = this.active || this.addedCriteria.length > 0;
				if (this.selectedCriteriumIndex >= this.addedCriteria.length) {
					this.selectedCriteriumIndex = this.addedCriteria.length - 1;
				}
			},
		},
		// Watch the hash since the query params object itself can update even if it's the same (such as when paginating)
		firstHitPreviewQueryHash: {
			immediate: true,
			handler() {
				if (!this.active) return;

				this.hits = undefined;
				if (this.firstHitPreviewQuery && this.type === 'hits') {
					blacklab.getHits(INDEX_ID, this.firstHitPreviewQuery).request.then(r => {
						const data = r as BLHitResults;
						if (isHitResults(data)) {
							// Make sure the target hits (otherFields) 'know' they are the target of a relation.
							mergeMatchInfos(data);
						}
						this.hits = data;
					});
				}
			}
		},
	},
});
